import React, { lazy, useState, useEffect } from "react";
import {
  Route,
  Routes,
  useLocation,
  Navigate,
  useNavigate,
  useNavigationType,
} from "react-router-dom";
import "./Style.css";
import { getLocalStorage } from "./utils/localstorage";

import "./style/questionnaire/ChooseTags.css";
import "./style/questionnaire/styles.css";
import routes from "./routes";
const PageNotFound = lazy(() => import("./pages/auth/PageNotFound"));
import { MainLayout } from "./Layout/MainLayout";
import { QuestionLayout } from "./Layout/QuestionLayout";

import { Header } from "./components/Header";
import InactiveAlert from "./components/InactiveAlert";
import { UserTypes } from "./models/UserTypes";
import { PreviewContext } from "./context/provider/PreviewContext";
import SmoothScroll from "./components/questionnaires/SmoothScroll/SmoothScroll";
import Log from "../src/utils/logger";
import { SingleTab } from "./components/SingleTab";

function SessionExpired() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#f5f5f5" }}>
      <div style={{ background: "#fff", borderRadius: 8, padding: "40px 48px", boxShadow: "0 2px 16px rgba(0,0,0,0.10)", textAlign: "center", maxWidth: 420 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏱</div>
        <h2 style={{ margin: "0 0 12px", fontSize: 22, color: "#222" }}>Your session has expired</h2>
        <p style={{ color: "#666", marginBottom: 28, lineHeight: 1.5 }}>
          You were logged out due to inactivity or an expired session. Please log in again to continue.
        </p>
        <a href="/auth/login" style={{ display: "inline-block", background: "#005eb8", color: "#fff", padding: "12px 32px", borderRadius: 6, textDecoration: "none", fontWeight: 600, fontSize: 15 }}>
          Log in again
        </a>
      </div>
    </div>
  );
}
function InitialPage() {
  return <Navigate to="/auth/login" />;
}

const initialFilterState = {
  searchText: "",
  services: [],
  categories: [],
  rightcareTriggers: [],
  safety: [],
  status: [],
  others: [],
  ageFrom: "",
  ageTo: "",
  dateFrom: "",
  dateTo: "",
};

function App() {
  const [preview, setPreview] = useState({ disabled: false });
  const loggedUser: any = getLocalStorage("userDetails");

  function RequireAuth({ children }: { children: JSX.Element }) {
    const location = useLocation();
    try {
      const userDetails: any = getLocalStorage("userDetails");
      if (userDetails && JSON.parse(userDetails)?.accessToken) {
        return children;
      }
      return <Navigate to="/auth/login" state={{ from: location }} replace />;
    } catch (error) {
      Log.error("An error occurred:", error);
      return <Navigate to="/auth/login" state={{ from: location }} replace />;
    }
  }
  const navigate = useNavigate();
  const location = useLocation();
  const nvtype = useNavigationType();

  useEffect(() => {
    if (nvtype === "POP" && document.referrer !== "about:blank") {
      const userDetails: any = getLocalStorage("userDetails");
      if (userDetails && JSON.parse(userDetails)?.accessToken) {
        if (JSON.parse(userDetails)?.usertype == "Client") {
          return navigate("/client/dashboard");
        }
      }
    }
  }, [location]);

  useEffect(() => {
    const bodyElement = document.querySelector("body");
    const userType = ["Admin", "Super_Admin"];
    if (!userType.includes(JSON.parse(loggedUser)?.usertype)) {
      bodyElement?.classList.add("client-block");
    } else {
      bodyElement?.classList.remove("client-block");
    }
    return () => {
      bodyElement?.classList.remove("client-block");
    };
  }, [loggedUser]);

  function RedirectToDashboard({ children }: { children: JSX.Element }) {
    try {
      const userDetails: any = getLocalStorage("userDetails");
      if (userDetails && JSON.parse(userDetails)?.accessToken) {
        if (JSON.parse(userDetails)?.usertype == "Client") {
          return <Navigate to="/client/dashboard" />;
        }
      }
      return children;
    } catch (error) {
      Log.error("An error occurred:", error);
      return children;
    }
  }

  let userDetails: any = getLocalStorage("userDetails");
  let Role = JSON.parse(userDetails)?.usertype;

  return (
    <PreviewContext.Provider value={{ preview, setPreview }}>
      <SmoothScroll>
        <Routes>
          <Route
            path="/"
            element={
              <RedirectToDashboard>
                <InitialPage />
              </RedirectToDashboard>
            }
          />

          {(userDetails = getLocalStorage("userDetails"))}
          {(Role = JSON.parse(userDetails)?.usertype)}

          {routes.map(
            ({
              url,
              requirePermission,
              roles,
              component: Component,
              title,
              heading,
            }: any) => {
              if (Role && requirePermission && roles?.indexOf(Role) > -1) {
                if (Role == UserTypes.admin || Role == UserTypes.superAdmin) {
                  return (
                    <Route
                      key={url}
                      path={url}
                      element={
                        <RequireAuth>
                          <>
                            <QuestionLayout>
                              <Component title={title} />
                            </QuestionLayout>
                          </>
                        </RequireAuth>
                      }
                    />
                  );
                } else {
                  return (
                    <Route
                      key={url}
                      path={url}
                      element={
                        <RequireAuth>
                          <MainLayout headertext={heading}>
                            <InactiveAlert />
                            <Component title={title} />
                          </MainLayout>
                        </RequireAuth>
                      }
                    />
                  );
                }
              }
              //If the Requested URL needs permission and the User Role is Undefined or null
              else if (requirePermission && !Role) {
                return (
                  <Route
                    key={url}
                    path={url}
                    element={
                      <RequireAuth>
                        <MainLayout headertext={heading}>
                          <InactiveAlert />
                          <Component title={title} />
                        </MainLayout>
                      </RequireAuth>
                    }
                  />
                );
              } else {
                return (
                  <Route
                    key={url}
                    path={url}
                    element={
                      <>
                        <Header probileshow />
                        <InactiveAlert />
                        <Component title={title} />
                      </>
                    }
                  />
                );
              }
            }
          )}
          <Route path="/session-expired" element={<SessionExpired />} />
          <Route path="/*" element={<PageNotFound />} />
        </Routes>
      </SmoothScroll>
      <SingleTab />
    </PreviewContext.Provider>
  );
}

export default App;
